import { useCallback, useEffect, useRef, useState } from 'react'
import {
  AnimatePresence,
  motion,
  useMotionValue,
  type PanInfo,
} from 'motion/react'
import '../styles.scss'
import ProgramSignupSheetContent from './ProgramSignupSheetContent'
import type { ProgramSignupSheetViewModel } from './useProgramSignupSheetViewModel'

type Props = {
  viewModel: ProgramSignupSheetViewModel
}

const SPRING = {
  type: 'spring' as const,
  damping: 32,
  stiffness: 380,
  mass: 0.8,
}
const DISMISS_THRESHOLD = 100

export default function ProgramSignupSheet({ viewModel }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const dragY = useMotionValue(0)
  const [isDraggingSheet, setIsDraggingSheet] = useState(false)
  const touchStartY = useRef(0)
  const touchStartScrollTop = useRef(0)

  // Close on Escape
  useEffect(() => {
    if (!viewModel.isSheetOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') viewModel.closeSheet()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [viewModel.isSheetOpen, viewModel.closeSheet])

  // Lock body scroll and pause background animations when open
  useEffect(() => {
    if (viewModel.isSheetOpen) {
      document.body.style.overflow = 'hidden'
      document
        .querySelectorAll('.program-signup-border-gradient')
        .forEach((el) => {
          ;(el as HTMLElement).style.animationPlayState = 'paused'
        })
      return () => {
        document.body.style.overflow = ''
        document
          .querySelectorAll('.program-signup-border-gradient')
          .forEach((el) => {
            ;(el as HTMLElement).style.animationPlayState = ''
          })
      }
    }
  }, [viewModel.isSheetOpen])

  // Intercept touch events on the scroll container to decide between
  // scrolling content vs dragging the sheet down to dismiss
  useEffect(() => {
    const scrollEl = scrollRef.current
    if (!scrollEl || !viewModel.isSheetOpen) return

    function handleTouchStart(e: TouchEvent) {
      touchStartY.current = e.touches[0].clientY
      touchStartScrollTop.current = scrollEl!.scrollTop
    }

    function handleTouchMove(e: TouchEvent) {
      const deltaY = e.touches[0].clientY - touchStartY.current
      const isSwipingDown = deltaY > 0
      const isAtTop = scrollEl!.scrollTop <= 0

      if (isAtTop && isSwipingDown && touchStartScrollTop.current <= 0) {
        // User is at top and pulling down — lock scroll so drag takes over
        e.preventDefault()
        setIsDraggingSheet(true)
      }
    }

    function handleTouchEnd() {
      setIsDraggingSheet(false)
    }

    // Must be non-passive to allow preventDefault
    scrollEl.addEventListener('touchstart', handleTouchStart, { passive: true })
    scrollEl.addEventListener('touchmove', handleTouchMove, { passive: false })
    scrollEl.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      scrollEl.removeEventListener('touchstart', handleTouchStart)
      scrollEl.removeEventListener('touchmove', handleTouchMove)
      scrollEl.removeEventListener('touchend', handleTouchEnd)
    }
  }, [viewModel.isSheetOpen])

  const handleDragEnd = useCallback(
    (_: unknown, info: PanInfo) => {
      setIsDraggingSheet(false)
      if (info.offset.y > DISMISS_THRESHOLD || info.velocity.y > 300) {
        viewModel.closeSheet()
      }
    },
    [viewModel.closeSheet],
  )

  // Prevent drag from moving the sheet when user is scrolling content
  const handleDrag = useCallback(
    (_: unknown, info: PanInfo) => {
      const scrollEl = scrollRef.current
      if (scrollEl && scrollEl.scrollTop > 0 && info.delta.y > 0) {
        dragY.set(0)
      }
    },
    [dragY],
  )

  return (
    <AnimatePresence>
      {viewModel.isSheetOpen && (
        <div key="program-signup-sheet" className="fixed inset-0 z-30">
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={viewModel.closeSheet}
          />

          {/* Sheet */}
          <motion.div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-[20px] flex flex-col"
            style={{
              maxHeight: '92vh',
              willChange: 'transform',
              y: dragY,
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={SPRING}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 bg-white rounded-t-[20px] shrink-0 cursor-grab active:cursor-grabbing">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>

            {/* Scrollable content — overflow is locked when dragging the sheet */}
            <div
              ref={scrollRef}
              className="flex-1 min-h-0 overscroll-contain"
              style={{
                overflowY: isDraggingSheet ? 'hidden' : 'auto',
                touchAction: isDraggingSheet ? 'none' : 'pan-y',
              }}
            >
              <div className="px-6 pb-8 pt-2">
                <ProgramSignupSheetContent viewModel={viewModel} />
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
